import { type Placement } from '@atlaskit/dropdown-menu/types';
import { type TriggerProps } from '@atlaskit/popup';
import { useAnalytics } from '@databases/analytics';
import { useCommentActionsContext } from '@databases/comments/hooks/useCommentContext';
import ContextualMenu from '@databases/contextual-menu/ContextualMenu';
import { type MenuData } from '@databases/contextual-menu/contextual-menu-state';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { type FieldTypeCategory } from '@databases/fields';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import { SUBMIT_SHORTCUT } from '@databases/keyboard-shortcuts/shortcuts';
import { useKeyboardShortcut } from '@databases/react';
import { getValueId } from '@databases/utils/utils';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { insertEntries } from './select-value-menu/actions/insert-entries';
import { insertFields } from './select-value-menu/actions/insert-fields';
import { submenu as submenuState } from './select-value-menu/state/submenu-state';
import { type MenuItemMap } from './select-value-menu/utils/create-contextual-menu-items';
import { groupMenuItemsInOrder } from './select-value-menu/utils/group-contextual-menu-items-in-order';
import { Position } from './type';

type SelectValueMenuProps = {
  contentId: string;
  entryId: string;
  fieldId: string;
  currentViewId: string;
  Trigger: (triggerProps: TriggerProps) => React.ReactNode;
  canEditContent: boolean;
  fieldTypeCategories?: FieldTypeCategory[];
  panel?: boolean;
  placement?: Placement;
  menuItems: MenuItemMap;
  canInsertContent: boolean;
  onCloseMenu: () => void;
};

const SelectValueMenu = React.memo(function SelectValueMenu({
  contentId,
  entryId,
  fieldId,
  currentViewId,
  Trigger,
  panel = false,
  placement = 'right-start',
  menuItems,
  canInsertContent,
  onCloseMenu,
}: SelectValueMenuProps) {
  const { formatMessage } = useIntl();
  const { setOpenThread } = useCommentActionsContext();
  const client = useAnalytics();
  const menuData: MenuData = {
    type: 'value',
    entryId,
    fieldId,
  };
  const { isMenuOpen } = useContextualMenu(menuData);
  const submenu = useSnapshot(submenuState.state);

  const formattedMenuItems = useMemo(() => {
    return groupMenuItemsInOrder(menuItems);
  }, [menuItems]);

  useKeyboardShortcut(
    isMenuOpen && !submenu.isOpen ? SHORTCUTS.OPEN_COMMENTS.keyboardShortCut : undefined,
    () => setOpenThread({ valueId: getValueId(entryId, fieldId), create: true, panel })
  );
  useKeyboardShortcut(
    isMenuOpen && !submenu.isOpen && canInsertContent
      ? SHORTCUTS.ADD_FIELD_RIGHT.keyboardShortCut
      : undefined,
    () =>
      insertFields({
        position: Position.AFTER,
        client,
        contentId,
        currentViewId,
        formatMessage,
      })
  );
  useKeyboardShortcut(
    isMenuOpen && !submenu.isOpen && canInsertContent
      ? SHORTCUTS.ADD_ENTRY_BELOW.keyboardShortCut
      : undefined,
    () =>
      insertEntries({
        position: Position.AFTER,
        client,
        contentId,
        formatMessage,
      })
  );
  useKeyboardShortcut(isMenuOpen && submenu.isOpen ? SUBMIT_SHORTCUT : undefined, () => {
    submenuState.close();
  });

  const handleOnCloseMenu = useCallback(() => {
    submenuState.close();

    onCloseMenu();
  }, [onCloseMenu]);

  return (
    <ContextualMenu
      menuData={menuData}
      items={formattedMenuItems}
      onClose={handleOnCloseMenu}
      trigger={Trigger}
      shouldDisableFocusLock // both needed to enable the initial focus of the flyout menu on hover
      shouldRenderToParent
      panel={panel}
      placement={placement}
    />
  );
});

export default SelectValueMenu;
